import React, { useState, useRef, useEffect } from "react";

interface EditableTextProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableText: React.FC<EditableTextProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onEdit(newValue); // Save in real-time
  };

  const closeInput = (shouldRevert: boolean) => {
    setIsEditing(false);
    if (shouldRevert) {
      setValue(text);
      onEdit(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.preventDefault();
      closeInput(e.key === "Escape");
    }
  };

  const handleBlur = () => {
    closeInput(false);
  };

  if (isEditing) {
    const originalStyle = window.getComputedStyle(
      document.querySelector(`.${className.split(" ")[0]}`) || document.body,
    );
    const originalFontSize = parseFloat(originalStyle.fontSize);

    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`bg-transparent border-b-2 border-white/30 focus:border-white/70 outline-none px-2 py-1 w-full ${className}`}
        style={{
          ...style,
          fontSize: `${originalFontSize * 0.95}px`,
          caretColor: "white",
          cursor: "text",
          transition: "all 0.2s ease",
        }}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`cursor-text ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
    >
      {text || <span className="opacity-50">{placeholder}</span>}
    </div>
  );
};

interface SlideContent {
  title?: string;
  subtitle?: string;
  objectives?: string;
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const LearningObjectivesSlide4 = {
  id: "learning-objectives4-slide",
  name: "Learning Objectives Slide (Floating Cards)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "objectives",
      label: "Learning Objectives (JSON array of { goal, description, icon })",
      type: "text",
      defaultValue: "[]",
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      teacherNote = "",
      bgColor = "",
      objectives = "[]",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    // Fixed parsing logic
    let parsedObjectives = [];
    try {
      if (typeof objectives === "string") {
        parsedObjectives = JSON.parse(objectives);
      } else if (Array.isArray(objectives)) {
        parsedObjectives = objectives;
      }
      // Ensure we have an array and limit to 8 items
      parsedObjectives = (
        Array.isArray(parsedObjectives) ? parsedObjectives : []
      ).slice(0, 8);
    } catch (error) {
      console.warn("Error parsing objectives:", error);
      parsedObjectives = [];
    }

    // Calculate layout based on number of objectives
    const getGridLayout = (count: number) => {
      if (count <= 3) return "grid-cols-1";
      if (count <= 6) return "grid-cols-2";
      return "grid-cols-3";
    };

    // Text sizes based on count - reduced sizes
    const getTextSizes = (count: number) => {
      if (count <= 3)
        return {
          goal: "text-xl",
          description: "text-base",
        };
      if (count <= 6)
        return {
          goal: "text-lg",
          description: "text-sm",
        };
      return {
        goal: "text-base",
        description: "text-xs",
      };
    };

    const gridClass = getGridLayout(parsedObjectives.length);
    const textSizes = getTextSizes(parsedObjectives.length);

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Minimal Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-white/5 rounded-full blur-3xl transform translate-x-1/4 -translate-y-1/4" />
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-white/5 rounded-full blur-3xl transform -translate-x-1/4 translate-y-1/4" />
        </div>

        {/* Content Container - reduced padding */}
        <div className="relative z-10 flex flex-col h-full p-6">
          {/* Header Section - reduced margins and text size */}
          <div className="text-center mb-6">
            <EditableText
              text={title}
              onEdit={handleEdit("title")}
              className="text-4xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/70"
              placeholder="Enter title..."
            />
            <div className="mt-2 relative">
              <EditableText
                text={subtitle}
                onEdit={handleEdit("subtitle")}
                className="text-xl font-light text-white/90"
                placeholder="Enter subtitle..."
              />
              {/* Decorative line under subtitle */}
              <div className="absolute left-1/2 -bottom-2 w-24 h-0.5 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -translate-x-1/2" />
            </div>
          </div>

          {/* Objectives Grid - adjusted spacing */}
          <div className={`grid ${gridClass} gap-4 flex-grow px-4`}>
            {parsedObjectives.map((obj, idx) => (
              <div key={idx} className="group relative">
                {/* Card Background with Gradient Border */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-white/10 to-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all duration-500">
                  {/* Top Section - reduced size */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center text-2xl transform group-hover:scale-110 transition-transform duration-500">
                      {obj.icon}
                    </div>
                    <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </div>
                  </div>

                  {/* Content - adjusted spacing */}
                  <div className="space-y-2">
                    <EditableText
                      text={obj.goal}
                      onEdit={(value) => {
                        const newObjectives = [...parsedObjectives];
                        newObjectives[idx] = { ...obj, goal: value };
                        handleEdit("objectives")(JSON.stringify(newObjectives));
                      }}
                      className={`font-bold ${textSizes.goal} text-white/90 leading-tight`}
                      placeholder={`Enter objective ${idx + 1}...`}
                    />
                    <EditableText
                      text={obj.description}
                      onEdit={(value) => {
                        const newObjectives = [...parsedObjectives];
                        newObjectives[idx] = { ...obj, description: value };
                        handleEdit("objectives")(JSON.stringify(newObjectives));
                      }}
                      className={`${textSizes.description} text-white/70 leading-relaxed`}
                      placeholder={`Enter description ${idx + 1}...`}
                    />
                  </div>

                  {/* Bottom Accent */}
                  <div className="absolute bottom-0 left-1/2 w-1/2 h-0.5 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-1/2 scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>
              </div>
            ))}
          </div>

          {/* Teacher Note - reduced margin */}
          {teacherNote && (
            <div className="relative mt-4 text-center">
              <div className="inline-flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
                <EditableText
                  text={teacherNote}
                  onEdit={handleEdit("teacherNote")}
                  className="text-base font-light italic text-white/80"
                  placeholder="Enter teacher note..."
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
};

// Declare global window interface
declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default LearningObjectivesSlide4;
